import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import ArrowLast from '@engineering/icons/arrow-last';
import ArrowNext from '@engineering/icons/arrow-next';
import Close from '@engineering/icons/close';
import { TooltipBadge } from '@silvertours/front-legacy-entities';
import { SearchInput } from './SearchInput';
import {
  getDefaultGridTemplateAreas,
  getDefaultWideGridTemplateAreas,
  getExtraLargeGridTemplateAreas,
  getLargeGridTemplateAreas,
  getMediumGridTemplateAreas,
} from './SearchFormLayout.styles';

type AffiliateProps = {
  $customColor?: string;
};

type ErrorPageProps = {
  $isErrorPage?: boolean;
};

type WideProps = {
  $isWide?: boolean;
};

const Wrapper = styled.section<WideProps>`
  position: ${({ $isWide }) => ($isWide ? '' : 'relative')};
  margin: ${({ $isWide }) => ($isWide ? '1.875rem auto' : '1.875rem 0')};
  background: ${({ $isWide }) => ($isWide ? '' : 'white')};
  max-width: ${({ $isWide }) => ($isWide ? '704px' : '')};
  width: ${({ $isWide }) => ($isWide ? '100%' : '')};

  ${({ theme }) => theme.mq('md')} {
    margin-block: 0.625rem;
    margin-right: ${({ $isWide }) => !$isWide && '0.625rem'};
    padding: 0.625rem 0.9375rem;
  }

  ${({ theme }) => theme.mq('lg')} {
    margin-block: 1rem;
    margin-right: ${({ $isWide }) => !$isWide && '1rem'};
    padding-inline: ${({ $isWide }) => ($isWide ? '0' : '')};
    max-width: ${({ $isWide }) => ($isWide ? 'none' : '')};
  }
`;

const Title = styled.strong<AffiliateProps & WideProps>`
  display: none;
  padding: ${({ $isWide }) => ($isWide ? '0 0 1.5rem' : '0.75rem 0 0.5rem')};
  font-family: ${({ theme }) => theme.fontFamily.alternate};
  font-size: ${({ $isWide }) => ($isWide ? '1.875rem' : '1.375rem')};
  font-weight: 400;
  line-height: 1.125;
  color: ${({ theme, $isWide }) =>
    $isWide ? theme.colors.neutral.white100 : ''};

  ${({ theme, $isWide }) => !$isWide && theme.mq('sm')} {
    max-width: 18.5rem;
  }

  ${({ theme, $isWide }) => $isWide && theme.mq('lg')} {
    font-size: 3rem;
  }

  ${({ $customColor }) =>
    $customColor &&
    css`
      padding-bottom: 1rem;
      color: ${$customColor};
      font-weight: 700;
    `}
`;

const Subtitle = styled.p<WideProps>`
  margin: ${({ $isWide }) => ($isWide ? '0 0 1rem' : '0 0 0.625rem')};
  color: ${({ theme, $isWide }) =>
    $isWide ? theme.colors.brand.green120 : theme.colors.neutral.grey160};
  font-family: ${({ theme, $isWide }) =>
    $isWide ? theme.fontFamily.alternate : ''};
  font-size: ${({ $isWide }) => ($isWide ? '1.375rem' : '1rem')};
  line-height: ${({ $isWide }) => ($isWide ? '1.625rem' : '')};
  text-wrap: pretty;

  ${({ theme, $isWide }) => $isWide && theme.mq('lg')} {
    font-size: 1.5rem;
    line-height: 1.25;
  }
`;

const StyledForm = styled.form<WideProps & ErrorPageProps>`
  position: ${({ $isWide }) => $isWide && 'relative'};
  display: ${({ $isWide }) => !$isWide && 'grid'};
  grid-template-areas: ${({ $isWide }) =>
    !$isWide && "'subtitle subtitle' 'form options'"};
  padding: ${({ $isWide }) => $isWide && '1.5rem 2rem'};
  border-radius: ${({ theme, $isWide }) => $isWide && theme.radius.md};
  border: ${({ $isErrorPage }) => $isErrorPage && '1px solid'};
  border-color: ${({ theme, $isErrorPage }) =>
    $isErrorPage && theme.colors.neutral.grey80};
  box-shadow: ${({ $isErrorPage }) =>
    $isErrorPage && '0px 4px 28px 0px #0000001F'};
  background-color: ${({ theme, $isWide }) =>
    $isWide && theme.colors.neutral.white100};
`;

const StyledBadge = styled(TooltipBadge)<WideProps>`
  top: ${({ $isWide }) => $isWide && '-0.5rem'};
  right: ${({ $isWide }) => $isWide && '-1rem'};

  ${({ theme, $isWide }) => $isWide && theme.mq('md')} {
    top: -1rem;
    right: 2rem;
  }

  background-color: ${({ theme }) => theme.colors.brand.orange80};
  background-image: none;
`;

const DepartureInput = styled(SearchInput)`
  grid-area: departure;
`;

const DestinationInput = styled(SearchInput)`
  grid-area: destination;
`;

const ErrorMessage = styled.div<WideProps>`
  grid-area: location-error;
  margin: ${({ $isWide }) =>
    $isWide ? '-0.5rem 1rem 1rem' : '-0.125rem 1rem 1rem'};
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.accent.red100};
`;

const LocationErrorMessage = styled(ErrorMessage)<WideProps>`
  grid-area: location-error;
`;

const DateErrorMessage = styled(ErrorMessage)<WideProps>`
  grid-area: date-error;
`;

type FormProps = {
  $hasDestinationInput: boolean;
  $hasMoreOptionsToggle: boolean;
  $hasLocationError: boolean;
  $hasDateError: boolean;
};

const FormContainer = styled.div<WideProps & FormProps>`
  grid-area: form;
  display: grid;
  grid-template-areas: ${({ $isWide, ...props }) =>
    $isWide
      ? getDefaultWideGridTemplateAreas(props)
      : getDefaultGridTemplateAreas(props)};
  grid-template-columns: ${({ $isWide }) =>
    $isWide ? 'repeat(2, 1fr)' : '1fr'};
  gap: ${({ $isWide }) => ($isWide ? '1rem' : '0.625rem')};
  width: ${({ $isWide }) => ($isWide ? '' : '505px')};

  ${({ theme, $isWide, ...props }) =>
    $isWide &&
    css`
      /* edge case for small tablets */
      @media (min-width: 634px) {
        grid-template-areas: ${getMediumGridTemplateAreas(props)};
      }

      ${theme.mq('lg')} {
        grid-template-areas: ${getLargeGridTemplateAreas(props)};
        grid-template-columns: repeat(4, 1fr);
      }

      /* edge case for slightly larger screens */
      @media (min-width: 1080px) {
        grid-template-areas: ${getExtraLargeGridTemplateAreas(props)};
      }

      /* error style overrides for wide layout */
      [role='alert'] {
        margin-bottom: 0;
      }
    `}
`;

const ButtonWrapper = styled.div<AffiliateProps>`
  grid-area: button;

  button {
    ${({ $customColor }) =>
      $customColor &&
      css`
        background-color: ${$customColor};
      `}
  }
`;

type FooterLinkProps = {
  iconOnLeft?: boolean;
};

const FooterLink = styled.div<FooterLinkProps>`
  grid-area: options-toggle;
  text-align: right;
  margin-bottom: 0;

  a {
    ${({ iconOnLeft }) => iconOnLeft && 'flex-direction: row-reverse;'}
  }
`;

const getOptionsStyles = (theme: Theme) => css`
  width: 0.75rem;
  height: 0.75rem;
  fill: ${theme.colors.brand.green120};
`;

const OptionsOpenIcon = styled(ArrowLast)`
  ${({ theme }) => getOptionsStyles(theme)};
`;

const OptionsCloseIcon = styled(ArrowNext)`
  ${({ theme }) => getOptionsStyles(theme)};
`;

const CloseIcon = styled(Close)`
  cursor: pointer;
`;

const TryAgainButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`;

export {
  ButtonWrapper,
  CloseIcon,
  DepartureInput,
  DestinationInput,
  FooterLink,
  FormContainer,
  OptionsCloseIcon,
  OptionsOpenIcon,
  StyledBadge,
  StyledForm,
  Subtitle,
  Title,
  Wrapper,
  LocationErrorMessage,
  DateErrorMessage,
  TryAgainButtonWrapper,
};
